/**
 * Tenant-aware Demo Data Resolver
 *
 * Returns the correct demo dataset based on the active tenant.
 * Import this instead of importing mockDataExtended directly.
 */

import { getTenantId, getTenantConfig } from '../tenants';
import type { PropertyCardData } from '../components/new-components/PropertyCard';
import type { CryptoAsset } from '../components/new-components/CryptoStaking.types';
import type {
  FundMetrics,
  FundraisingProgress,
  PerformanceDataPoint,
  AllocationItem,
  FundHighlight,
} from '../components/new-components/HiveFund.types';

// ── Honeyshares data (existing) ──────────────────────────────────────────────
import {
  EXTENDED_MOCK_PROPERTIES as HS_PROPERTIES,
  DEMO_PORTFOLIO_HOLDINGS as HS_HOLDINGS,
  DEMO_DASHBOARD_SUMMARY as HS_SUMMARY,
  DEMO_CRYPTO_ASSETS as HS_CRYPTO,
  DEMO_CRYPTO_NETWORK_FEE as HS_CRYPTO_FEE,
  DEMO_HIVE_FUND_METRICS as HS_FUND_METRICS,
  DEMO_HIVE_FUND_FUNDRAISING as HS_FUND_FUNDRAISING,
  DEMO_HIVE_FUND_PERFORMANCE as HS_FUND_PERFORMANCE,
  DEMO_HIVE_FUND_ASSET_ALLOCATION as HS_FUND_ALLOCATION,
  DEMO_HIVE_FUND_GEO_DISTRIBUTION as HS_FUND_GEO,
  DEMO_HIVE_FUND_HIGHLIGHTS as HS_FUND_HIGHLIGHTS,
  DEMO_HIVE_FUND_DESCRIPTION as HS_FUND_DESCRIPTION,
  DEMO_HIVE_FUND_DISCLAIMER as HS_FUND_DISCLAIMER,
} from './mockDataExtended';

// ── REDEX data ───────────────────────────────────────────────────────────────
import {
  REDEX_DEMO_PROPERTIES,
  REDEX_DEMO_PORTFOLIO_HOLDINGS,
  REDEX_DEMO_DASHBOARD_SUMMARY,
  REDEX_DEMO_CRYPTO_ASSETS,
  REDEX_DEMO_CRYPTO_NETWORK_FEE,
  REDEX_DEMO_FUND_METRICS,
  REDEX_DEMO_FUND_FUNDRAISING,
  REDEX_DEMO_FUND_PERFORMANCE,
  REDEX_DEMO_FUND_ASSET_ALLOCATION,
  REDEX_DEMO_FUND_GEO_DISTRIBUTION,
  REDEX_DEMO_FUND_HIGHLIGHTS,
  REDEX_DEMO_FUND_DESCRIPTION,
  REDEX_DEMO_FUND_DISCLAIMER,
} from './mockDataRedex';

// ── Resolved shape ───────────────────────────────────────────────────────────

export interface TenantDemoData {
  properties: PropertyCardData[];
  portfolioHoldings: typeof HS_HOLDINGS;
  dashboardSummary: typeof HS_SUMMARY;
  cryptoAssets: CryptoAsset[];
  cryptoNetworkFee: { amount: number; isEstimate: boolean };
  fundMetrics: FundMetrics;
  fundFundraising: FundraisingProgress;
  fundPerformance: PerformanceDataPoint[];
  fundAssetAllocation: AllocationItem[];
  fundGeoDistribution: AllocationItem[];
  fundHighlights: FundHighlight[];
  fundDescription: string;
  fundDisclaimer: string;
  /** Label for the "fund" entity shown in Fund tab header, e.g. "Hive Fund" or "REDEX Growth Fund" */
  fundDisplayName: string;
  /** Invest-tab categories (passed to FilterBar) */
  investCategories: { value: string; label: string }[];
}

const HONEYSHARES_DATA: TenantDemoData = {
  properties: HS_PROPERTIES,
  portfolioHoldings: HS_HOLDINGS,
  dashboardSummary: HS_SUMMARY,
  cryptoAssets: HS_CRYPTO,
  cryptoNetworkFee: HS_CRYPTO_FEE,
  fundMetrics: HS_FUND_METRICS,
  fundFundraising: HS_FUND_FUNDRAISING,
  fundPerformance: HS_FUND_PERFORMANCE,
  fundAssetAllocation: HS_FUND_ALLOCATION,
  fundGeoDistribution: HS_FUND_GEO,
  fundHighlights: HS_FUND_HIGHLIGHTS,
  fundDescription: HS_FUND_DESCRIPTION,
  fundDisclaimer: HS_FUND_DISCLAIMER,
  fundDisplayName: 'Hive Fund',
  investCategories: [
    { value: 'all', label: 'All' },
    { value: 'single-family', label: 'Single Family' },
    { value: 'vacation-rental', label: 'Vacation Rental' },
    { value: 'multi-family', label: 'Multi-Family' },
    { value: 'fund', label: 'Funds' },
  ],
};

const REDEX_DATA: TenantDemoData = {
  properties: REDEX_DEMO_PROPERTIES,
  portfolioHoldings: REDEX_DEMO_PORTFOLIO_HOLDINGS,
  dashboardSummary: REDEX_DEMO_DASHBOARD_SUMMARY,
  cryptoAssets: REDEX_DEMO_CRYPTO_ASSETS,
  cryptoNetworkFee: REDEX_DEMO_CRYPTO_NETWORK_FEE,
  fundMetrics: REDEX_DEMO_FUND_METRICS,
  fundFundraising: REDEX_DEMO_FUND_FUNDRAISING,
  fundPerformance: REDEX_DEMO_FUND_PERFORMANCE,
  fundAssetAllocation: REDEX_DEMO_FUND_ASSET_ALLOCATION,
  fundGeoDistribution: REDEX_DEMO_FUND_GEO_DISTRIBUTION,
  fundHighlights: REDEX_DEMO_FUND_HIGHLIGHTS,
  fundDescription: REDEX_DEMO_FUND_DESCRIPTION,
  fundDisclaimer: REDEX_DEMO_FUND_DISCLAIMER,
  fundDisplayName: 'REDEX Growth Fund',
  investCategories: [
    { value: 'all', label: 'All' },
    { value: 'license', label: 'Licenses' },
    { value: 'mining', label: 'Mining' },
    { value: 'fund', label: 'Funds' },
    { value: 'debt', label: 'Debt' },
    { value: 'development', label: 'Infrastructure' },
  ],
};

/**
 * Returns the demo dataset that matches the active tenant build.
 */
export function getTenantDemoData(): TenantDemoData {
  const id = getTenantId();
  switch (id) {
    case 'redex':
      return REDEX_DATA;
    case 'honeyshares':
    default:
      return HONEYSHARES_DATA;
  }
}
